import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { makeApiCall } from '../utils/customerUtils'
import { PageContent } from '../components/Layout'
import { SearchIcon, UserIcon, BriefcaseIcon, PhoneIcon, MessageSquareIcon, ClockIcon } from '../components/Icons'

interface SearchResult {
  id: string
  type: 'customer' | 'deal' | 'lead' | 'comment' | 'phone' | 'task'
  title: string
  subtitle: string
  description?: string
  url: string
  metadata?: {
    status?: string
    statusColor?: string
    date?: string
    amount?: string
  }
}

const SearchResults: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [results, setResults] = useState<SearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')

  useEffect(() => {
    const searchQuery = searchParams.get('q') || ''
    setQuery(searchQuery)
    
    if (searchQuery) {
      performSearch(searchQuery)
    }
  }, [searchParams])

  const performSearch = async (searchQuery: string) => {
    setIsLoading(true)
    setError(null)
    
    try {
      const response = await makeApiCall(`/api/search/global?q=${encodeURIComponent(searchQuery)}&limit=50`)
      if (response.ok) {
        const data = await response.json()
        setResults(data.results || [])
      } else {
        setError('Failed to search. Please try again.')
      }
    } catch (error) {
      console.error('Search error:', error)
      setError('An error occurred while searching. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const getResultIcon = (type: string) => {
    switch (type) {
      case 'customer': return UserIcon
      case 'deal': return BriefcaseIcon
      case 'lead': return PhoneIcon
      case 'comment': return MessageSquareIcon
      case 'phone': return PhoneIcon
      case 'task': return ClockIcon
      default: return SearchIcon
    }
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'customer': return 'Customer'
      case 'deal': return 'Deal'
      case 'lead': return 'Lead'
      case 'comment': return 'Comment'
      case 'phone': return 'Phone Number'
      case 'task': return 'Task'
      default: return 'Result'
    }
  }

  const formatResultTitle = (result: SearchResult) => {
    if (result.type === 'comment') {
      return `Comment in ${result.title}`
    }
    return result.title
  }

  const handleResultClick = (result: SearchResult) => {
    navigate(result.url)
  }

  const groupResultsByType = (results: SearchResult[]) => {
    const grouped: Record<string, SearchResult[]> = {}
    
    results.forEach(result => {
      if (!grouped[result.type]) {
        grouped[result.type] = []
      }
      grouped[result.type].push(result)
    })
    
    return grouped
  }

  const groupedResults = groupResultsByType(results)

  return (
    <PageContent>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Search Results
            </h1>
            {query && (
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {isLoading ? 'Searching...' : `Found ${results.length} results for "${query}"`}
              </p>
            )}
          </div>
        </div>

        {/* Search Input */}
        <div className="max-w-2xl">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SearchIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  navigate(`/dashboard/search?q=${encodeURIComponent(query)}`)
                }
              }}
              placeholder="Search customers, deals, leads, comments..."
              className="block w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Searching...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* No Results */}
        {!isLoading && !error && results.length === 0 && query && (
          <div className="text-center py-12">
            <SearchIcon className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No results found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Try different keywords or check your spelling
            </p>
          </div>
        )}

        {/* Results */}
        {!isLoading && !error && results.length > 0 && (
          <div className="space-y-8">
            {Object.entries(groupedResults).map(([type, typeResults]) => {
              const IconComponent = getResultIcon(type)
              return (
                <div key={type} className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <IconComponent className="h-5 w-5 text-gray-400" />
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {getTypeLabel(type)} ({typeResults.length})
                    </h2>
                  </div>
                  
                  <div className="grid gap-4">
                    {typeResults.map((result) => (
                      <div
                        key={result.id}
                        onClick={() => handleResultClick(result)}
                        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md hover:border-blue-300 dark:hover:border-gray-900 transition-all duration-200 cursor-pointer"
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-1">
                            <IconComponent className="h-5 w-5 text-gray-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {formatResultTitle(result)}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-1">
                              {result.subtitle}
                            </p>
                            {result.description && (
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 line-clamp-2">
                                {result.description}
                              </p>
                            )}
                            {result.metadata && (
                              <div className="flex items-center space-x-3 mt-2">
                                {result.metadata.status && (
                                  <span 
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                                    style={{ 
                                      backgroundColor: result.metadata.statusColor + '20',
                                      color: result.metadata.statusColor 
                                    }}
                                  >
                                    {result.metadata.status}
                                  </span>
                                )}
                                {result.metadata.amount && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {result.metadata.amount}
                                  </span>
                                )}
                                {result.metadata.date && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {result.metadata.date}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </PageContent>
  )
}

export default SearchResults
